import { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import moment from "moment";
import { getMemberTrackersApi } from "src/DAL/tracker/tracker";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrButton from "src/design-system/components/GrButton";
import GrBadge from "src/design-system/components/GrBadge";
import GrText from "src/design-system/components/GrText";
import GrIcon from "src/design-system/components/GrIcon";
import GrListRow from "src/design-system/components/GrListRow";
import { GrProgressBar } from "src/design-system/components/GrProgress";
import {
  getStepDate,
  groupMemberTrackers,
  isLeftTracker,
  relativeDayLabel,
} from "src/pages/Trackers/memberTrackersStorage";
import { JourneyStatusBadge } from "src/pages/Trackers/MemberJourneyParts";

/**
 * Dashboard "Your Journeys" (approved design, 28 Oct): "To do now" lists every journey whose
 * check-in can be sent now, each with its Check-in button; "On track" lists the rest with where the
 * member is and the next check-in. The first six rows are in view and the rest scroll inside the
 * card (2 Nov). No combined total: ongoing journeys have none, so one would mislead. The full
 * listing stays on /trackers. Not shown while loading or when there is no journey.
 */

// Which journeys the dashboard lists: missed, then open, then the rest; completed, left and
// password-locked journeys are not listed.
const RANK = { overdue: 0, open: 1, up_to_date: 2 };
const pickJourneys = (trackers) =>
  groupMemberTrackers(trackers)
    .my.filter(
      ({ tracker, meta }) =>
        !meta.isPasswordLocked &&
        !isLeftTracker(tracker) &&
        meta.summary?.status !== "completed"
    )
    .sort(
      (a, b) =>
        (RANK[a.meta.summary?.status] ?? 9) - (RANK[b.meta.summary?.status] ?? 9) ||
        new Date(b.tracker.lastSubmittedAt || 0) - new Date(a.tracker.lastSubmittedAt || 0)
    );

// Same rule as the journey page: the open check-in, else the last one still waiting.
const checkInTarget = (summary) => {
  const lateTarget = !summary.open ? summary.lastOverdue : null;
  return summary.open || lateTarget ? { stepId: lateTarget ? lateTarget.id : null } : null;
};

// Rows in view before the list scrolls inside the card.
const ROWS_IN_VIEW = 6;

/**
 * Height of the list's first `rows` rows (section labels included), measured, since to-do and
 * on-track rows differ in height; null while everything fits. Re-measured when the width changes
 * (rows wrap) and when `listKey` (the listed journeys) changes.
 */
const useRowsHeight = (ref, rows, listKey) => {
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return undefined;
    const measure = () => {
      const items = box.querySelectorAll("li");
      if (items.length <= rows) {
        setHeight(null);
        return;
      }
      const last = items[rows - 1].getBoundingClientRect();
      setHeight(Math.ceil(last.bottom - box.getBoundingClientRect().top + box.scrollTop));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [ref, rows, listKey]);
  return height;
};

const day = (value) => moment(value).format("ddd D MMM");

// Wording copied from the earlier dashboard card (DashboardJourneyCard, removed): where the
// member is in a limited journey's time.
const timeText = (summary) => {
  if (summary.ended) return "Finished";
  return `Week ${Math.min(summary.weekNumber, summary.weekTotal)} of ${summary.weekTotal}`;
};

// Wording copied from the earlier dashboard card: the one thing to know next.
const nextLine = (summary) => {
  if (summary.overdue.length > 1) {
    return `${summary.overdue.length} check-ins are still waiting — you can send them now`;
  }
  if (summary.overdue.length) {
    return `Check-in from ${day(getStepDate(summary.overdue[0]))} is still waiting — you can send it now`;
  }
  if (summary.open) {
    return summary.openToday
      ? "Check-in due today"
      : `Check-in from ${day(getStepDate(summary.open))} is open`;
  }
  if (summary.next && getStepDate(summary.next)) {
    const date = getStepDate(summary.next);
    return `Next check-in ${day(date)} (${relativeDayLabel(date)})`;
  }
  if (summary.total && summary.doneCount >= summary.total) return "All check-ins done";
  return null;
};

const titleSx = {
  display: "inline-flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: gr.space[2],
};
const labelSx = { fontWeight: gr.font.weight.medium, mb: gr.space[2] };
const listSx = { m: 0, p: 0 };
const todoListSx = { ...listSx, display: "flex", flexDirection: "column", gap: gr.space[2] };
// Soft highlighted row; keeps its own tint on hover (a thin late ring says it is clickable).
const todoRowSx = {
  mx: 0,
  px: gr.space[4],
  py: gr.space[3],
  background: gr.color.lateSoft,
  "& + &": { borderTop: 0 },
  "&:hover": { background: gr.color.lateSoft, boxShadow: `inset 0 0 0 1px ${gr.color.late}` },
};
const lineSx = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const progressSx = { display: "flex", alignItems: "center", gap: gr.space[2], my: "2px" };
const countSx = { flex: "none", fontVariantNumeric: "tabular-nums" };
const caughtUpSx = { display: "flex", alignItems: "center", gap: gr.space[2], mb: gr.space[4] };

function TodoRow({ item, primary, onOpen, onCheckIn }) {
  const { tracker, meta } = item;
  const name = tracker.name || "Journey";
  return (
    // Not compact: the reminder must be readable in full, so on phones its text wraps and the
    // Check-in button gets its own line under it.
    <GrListRow
      as="li"
      onClick={onOpen}
      openLabel={`Open ${name}`}
      icon={<GrIcon icon={icons.clock} />}
      iconTone="late"
      title={name}
      trailing={<JourneyStatusBadge summary={meta.summary} size="sm" />}
      meta={nextLine(meta.summary)}
      action={
        <GrButton
          size="sm"
          // One primary per screen: only the first to-do gets it.
          variant={primary ? "primary" : "outline"}
          aria-label={`Check-in: ${name}`}
          onClick={onCheckIn}
        >
          Check-in
        </GrButton>
      }
      sx={todoRowSx}
    />
  );
}

function OnTrackRow({ item, onOpen }) {
  const { tracker, meta } = item;
  const summary = meta.summary;
  const name = tracker.name || "Journey";
  const limited = Boolean(summary.weekTotal);
  const where = limited ? timeText(summary) : tracker.cadenceLabel;
  const next = nextLine(summary);
  const doneText = `${summary.doneCount} ${summary.doneCount === 1 ? "check-in" : "check-ins"} done`;

  return (
    <GrListRow
      as="li"
      compact
      onClick={onOpen}
      openLabel={`Open ${name}`}
      icon={<GrIcon icon={icons.check} />}
      iconTone="ok"
      title={name}
      trailing={
        where ? (
          <GrText as="span" variant="secondary">
            · {where}
          </GrText>
        ) : null
      }
      meta={
        limited ? (
          <>
            {summary.total ? (
              <Box sx={progressSx}>
                <GrProgressBar
                  value={summary.percent}
                  ariaLabel={`${name}: ${summary.doneCount} of ${summary.total} check-ins done`}
                  sx={{ flex: 1 }}
                />
                <GrText as="span" variant="secondary" sx={countSx}>
                  {summary.doneCount}/{summary.total}
                </GrText>
              </Box>
            ) : null}
            {next ? <Box sx={lineSx}>{next}</Box> : null}
          </>
        ) : (
          [doneText, next].filter(Boolean).join(" · ")
        )
      }
      chevron
    />
  );
}

// The journeys the dashboard lists: loaded once by the dashboard page, which passes them to this card.
export const useDashboardJourneys = () =>
  useQuery({
    queryKey: ["dashboardJourneys"],
    queryFn: async () => {
      const res = await getMemberTrackersApi();
      if (res?.code !== 200) throw new Error(res?.message);
      return res.trackers || [];
    },
    select: pickJourneys,
  });

/** Journeys with a check-in the member can send now ("To do now"). */
const journeysToDo = (journeys) =>
  journeys.filter((item) => checkInTarget(item.meta.summary));

export default function DashboardJourneysSummary({ journeys = [] }) {
  const navigate = useNavigate();
  const listRef = useRef(null);
  const listHeight = useRowsHeight(
    listRef,
    ROWS_IN_VIEW,
    journeys.map((item) => item.tracker.id).join(",")
  );

  if (!journeys.length) return null;

  const todo = journeysToDo(journeys);
  const onTrack = journeys.filter((item) => !checkInTarget(item.meta.summary));

  const openJourney = (id) => navigate(`/trackers/${id}`);
  const checkIn = (id, stepId) =>
    navigate(`/trackers/${id}/submit${stepId ? `?step=${encodeURIComponent(stepId)}` : ""}`);

  return (
    <GrCard>
      <GrCardHeader
        as="h2"
        title={
          <Box component="span" sx={titleSx}>
            Your Journeys
            {/* Journeys listed here (completed and left ones are not counted). */}
            <GrBadge tone="brand">
              {journeys.length} {journeys.length === 1 ? "journey" : "journeys"}
            </GrBadge>
          </Box>
        }
        action={
          <GrButton variant="link" onClick={() => navigate("/trackers")}>
            All journeys →
          </GrButton>
        }
        wrapAction
      />

      {/* The first ROWS_IN_VIEW rows in view; the rest scroll inside the card. */}
      <Box
        ref={listRef}
        sx={
          listHeight
            ? { maxHeight: listHeight, overflowY: "auto", overscrollBehavior: "contain" }
            : undefined
        }
      >
        {todo.length ? (
          <Box component="section">
            <GrText as="h3" variant="caption" sx={labelSx}>
              To do now
            </GrText>
            <Box component="ul" sx={todoListSx}>
              {todo.map((item, index) => (
                <TodoRow
                  key={item.tracker.id}
                  item={item}
                  primary={index === 0}
                  onOpen={() => openJourney(item.tracker.id)}
                  onCheckIn={() =>
                    checkIn(item.tracker.id, checkInTarget(item.meta.summary).stepId)
                  }
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={caughtUpSx}>
            <Box component="span" sx={{ display: "inline-flex", color: gr.color.ok }}>
              <GrIcon icon={icons.checkCircle} />
            </Box>
            <GrText>You're all caught up</GrText>
          </Box>
        )}

        {onTrack.length ? (
          <Box component="section" sx={todo.length ? { mt: gr.space[4] } : undefined}>
            <GrText as="h3" variant="caption" sx={labelSx}>
              On track
            </GrText>
            <Box component="ul" sx={listSx}>
              {onTrack.map((item) => (
                <OnTrackRow
                  key={item.tracker.id}
                  item={item}
                  onOpen={() => openJourney(item.tracker.id)}
                />
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </GrCard>
  );
}

DashboardJourneysSummary.propTypes = {
  /** The listed journeys from useDashboardJourneys (nothing renders while empty or loading). */
  journeys: PropTypes.arrayOf(PropTypes.object),
};
